import { createAction } from '@reduxjs/toolkit';

import { urlUtils } from '../../utils/url';
import { fetchLabels } from '../../services/content';
import { logSearch, logProductImpression } from '../../utils/logUtils/CruiseListLogUtils';
import { fetchCruises, fetchCruisesForInspiration } from '../../services/cruise';

// **A hand-written thunk, not `createAsyncThunk`** (refined D1). `init` has four exits and they
// are not interchangeable: a non-200 adds an error message and then *carries on* to the normal
// finish, a translated message adds a message and carries on, an empty package list finishes
// through `initFailed`, and a throw finishes through a path that dispatches `initFinished`
// **first** and the message **second** — the reverse of the happy path's order.
//
// Every action below is dispatched by the thunk, so none can be slice-generated: the slice would
// import this module for the shared actions while this module imported the slice for its own, and
// a `createSlice` evaluated inside that cycle reads `undefined`. `initSucceeded` is read by four
// slices and the URL listener, so it is shared regardless.
//
// The twelve sort and filter actions are the opposite case — no thunk dispatches them — so they
// are slice-generated and live on `reducers/cruiseList/sort.js` and `.../filter.js`. The URL
// listener matches them from `middleware/cruiseListQuery.js`, which imports the slices; that is
// not a cycle, because the slices do not import the listener.
export const initSucceeded = createAction('cruiseList/initSucceeded');
export const initFailed = createAction('cruiseList/initFailed');
export const initFinished = createAction('cruiseList/initFinished');
export const messageAdded = createAction('cruiseList/messageAdded');

// Was a thunk returned by a thunk. Flattened into helpers taking `dispatch`, as steps 4 and 6 did.
//
// The session-id URL write stays **before** the dispatch. It used to sit in the action creator's
// body rather than in the returned thunk, so it ran while `dispatch(initSucceeded(...))` was
// evaluating its argument — that is, immediately before the action reached the reducers. Keeping
// it on this line preserves that order, which matters because the URL listener writes the
// pre-selected filter state on the same action, after the reducers.
//
// `data.result.serviceCallInformation.sessionId` is read unguarded, as before.
const handleInitSuccess = (dispatch, labels, data, siteSettings) => {
  urlUtils.updateUrlWithSessionId(data.result.serviceCallInformation.sessionId);

  dispatch(
    initSucceeded({
      labels,
      cruises: data.result.packages,
      filter: data.result.filterOptions,
      preSelectedFilter: siteSettings.filter,
      searchInformation: data.result.searchInformation,
    }),
  );
};

const handleInitFinished = (dispatch, labels, data, siteSettings) => {
  const { packages, loggingData } = data?.result ?? {};
  if (packages && packages.length > 0) {
    handleInitSuccess(dispatch, labels, data, siteSettings);
    logProductImpression(packages);
  } else {
    dispatch(initFailed({ labels }));
  }

  if (loggingData) {
    logSearch(loggingData, data?.translatedMessage);
  }

  dispatch(initFinished());
};

const getLabels = async (siteSettings) => {
  const labels = await fetchLabels(siteSettings, 'iwCruiseList');
  const { general, ...rest } = labels;
  return {
    ...rest,
    ...general,
  };
};

const getCruises = async (siteSettings, userId) => {
  const { isInspiration } = siteSettings;
  if (isInspiration) {
    return fetchCruisesForInspiration(siteSettings, userId);
  }
  return fetchCruises(siteSettings, userId);
};

// The catch path dispatches `initFinished` **before** the message, where the happy path dispatches
// it after. Preserved.
const handleCruiseInitError = (dispatch) => {
  dispatch(initFinished());
  dispatch(
    messageAdded({
      title: 'An error occurred, please redo your search.',
      severity: 'ERROR',
    }),
  );
};

export const init = () => async (dispatch, getState) => {
  // Was `getState().toJS().common` — the root is plain from this step on.
  const { siteSettings, userId } = getState().common;

  try {
    const [labels, cruisesResult] = await Promise.all([getLabels(siteSettings), getCruises(siteSettings, userId)]);

    // If cruise search failed, show error message
    if (cruisesResult.statusCode !== 200) {
      dispatch(
        messageAdded({
          title: labels ? labels.iwErrors?.systemError?.heading : 'An error occurred, please redo your search.',
          severity: 'ERROR',
        }),
      );
    }

    // Show translatedMessage from API
    if (cruisesResult.translatedMessage) {
      dispatch(messageAdded(cruisesResult.translatedMessage));
    }

    handleInitFinished(dispatch, labels, cruisesResult, siteSettings);
  } catch (_error) {
    // If unexpected error occurred, show error message.
    handleCruiseInitError(dispatch);
  }
};
